/**
 * Controlli di invio: "Invia ora" vs "Programma" (RF-04 / D5).
 * Fuso orario fisso Europe/Rome (RB-4): nessun selettore TZ, si usa dayjs locale `it`.
 *
 * Espone i valori al parent via callback; la composizione di `scheduled_at` (ISO8601)
 * è responsabilità del parent al submit.
 *
 * Riferimento: TDD Sprint 18 §8.6.2.
 */
import PropTypes from 'prop-types';
import dayjs from 'dayjs';
import 'dayjs/locale/it';
import { Box, Card, CardHeader, Grid, FormControlLabel, Radio, RadioGroup } from '@mui/material';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { CustomDatePicker, CustomTimeField } from '../../../../utils/StyledInputs';
import { isToday, minScheduleDate, minScheduleDateTime } from '../../../../utils/newsletter/schedule';

dayjs.locale('it');

/**
 * @param {{
 *   mode: 'now'|'schedule',
 *   onModeChange: (mode: 'now'|'schedule') => void,
 *   date: import('dayjs').Dayjs|null,
 *   time: import('dayjs').Dayjs|null,
 *   onDateChange: (d: import('dayjs').Dayjs|null) => void,
 *   onTimeChange: (t: import('dayjs').Dayjs|null) => void,
 *   disabled?: boolean,
 *   errors?: { date?: boolean, time?: boolean },
 * }} props
 */
export function ScheduleControls({
  mode,
  onModeChange,
  date,
  time,
  onDateChange,
  onTimeChange,
  disabled = false,
  errors = {},
}) {
  const isSchedule = mode === 'schedule';
  const dateIsToday = isToday(date);

  return (
    <Card sx={{ padding: '24px' }}>
      <CardHeader sx={{ padding: 0, mb: 2 }} title="Invio" />

      <RadioGroup
        row
        value={mode}
        onChange={(e) => onModeChange(e.target.value)}
      >
        <FormControlLabel value="now" disabled={disabled} control={<Radio />} label="Invia ora" />
        <FormControlLabel value="schedule" disabled={disabled} control={<Radio />} label="Programma" />
      </RadioGroup>

      {isSchedule && (
        <Box sx={{ paddingTop: '12px' }}>
          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale="it">
                {/* minDate esplicito su OGGI: `disablePast` da solo, confrontando l'istante
                    e non il giorno, finiva per escludere la giornata in corso. */}
                <CustomDatePicker
                  disabled={disabled}
                  minDate={minScheduleDate()}
                  label="Data invio *"
                  value={date}
                  error={!!errors.date}
                  onChange={onDateChange}
                />
              </LocalizationProvider>
            </Grid>
            <Grid item xs={12} md={6}>
              <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale="it">
                {/* Solo se si programma per oggi l'orario ha un minimo: ora + LEAD_MINUTES. */}
                <CustomTimeField
                  disabled={disabled}
                  minTime={dateIsToday ? minScheduleDateTime() : undefined}
                  label="Orario invio *"
                  value={time}
                  error={!!errors.time}
                  onChange={onTimeChange}
                  format="HH:mm"
                  helperText={dateIsToday ? `Non prima delle ${minScheduleDateTime().format('HH:mm')}` : undefined}
                />
              </LocalizationProvider>
            </Grid>
          </Grid>
        </Box>
      )}
    </Card>
  );
}

ScheduleControls.propTypes = {
  mode: PropTypes.oneOf(['now', 'schedule']).isRequired,
  onModeChange: PropTypes.func.isRequired,
  date: PropTypes.object,
  time: PropTypes.object,
  onDateChange: PropTypes.func.isRequired,
  onTimeChange: PropTypes.func.isRequired,
  disabled: PropTypes.bool,
  errors: PropTypes.object,
};

export default ScheduleControls;